/**
 * Reload once when a new service worker takes over.
 *
 * The service worker calls skipWaiting() and clientsClaim(), so a new build
 * activates and takes control as soon as it is fetched. What that does NOT do
 * is change the page already running: the old JavaScript keeps executing until
 * something reloads it. And because index.html is served from the precache, a
 * casual refresh can still hand back the previous build — so a deploy could
 * need two reloads before it appeared, or simply never appear while the tab
 * stayed open.
 *
 * `controllerchange` fires exactly when the new worker assumes control, which
 * is the first moment a reload is guaranteed to pick up the new build.
 */
/** How often an open tab asks whether a newer build exists. */
const UPDATE_CHECK_MS = 60_000;

export function reloadOnServiceWorkerUpdate(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // The event can fire more than once; without this guard the page would
    // reload in a loop, which is a far worse failure than a stale tab.
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  /*
   * Ask for a new worker on a timer, and whenever the tab is looked at again.
   *
   * A browser only re-fetches sw.js on navigation, or roughly once a day. A tab
   * left open on the dashboard would therefore never notice a deploy, however
   * correct the caching headers are — which is why new builds kept needing a
   * manual hard refresh. `update()` is the explicit ask; sw.js is served
   * no-store, so it is a real network check rather than a cache hit.
   */
  void navigator.serviceWorker.ready.then((registration) => {
    const check = () => {
      if (document.visibilityState === 'visible') void registration.update();
    };
    setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener('visibilitychange', check);
  });

  // Readable in the console, so "which build am I on" never needs guessing.
  console.info(`Pipyar build ${__APP_VERSION__}`);
}
